import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { heroSliderContent } from '@/data/i18n/heroSliderContent';

const heroImages = [
  '/cards/hero/drivers-license.webp',
  '/cards/hero/boater-license.webp',
  '/cards/hero/press-id.webp',
  '/cards/hero/photo-id.webp',
  '/cards/hero/state-id.webp',
  '/cards/hero/student-id.webp',
  '/cards/hero/national-id.webp',
];

const heroImagePreloadCache = new Map<string, Promise<void>>();

const preloadHeroImage = (src: string) => {
  const cached = heroImagePreloadCache.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const img = new Image();

    img.onload = () => {
      if (typeof img.decode === 'function') {
        img.decode().catch(() => undefined).finally(finish);
        return;
      }
      finish();
    };

    img.onerror = finish;
    img.src = src;

    if (img.complete) {
      if (typeof img.decode === 'function') {
        img.decode().catch(() => undefined).finally(finish);
        return;
      }
      finish();
    }
  });

  heroImagePreloadCache.set(src, promise);
  return promise;
};

export const HeroProductSlider: React.FC = () => {
  const { language } = useLanguage();
  const c = heroSliderContent[language] || heroSliderContent.en;
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'exit' | 'enter'>('idle');
  const [userInteracting, setUserInteracting] = useState(false);
  const interactionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayedIndexRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const isMountedRef = useRef(true);

  const currentProduct = c.products[displayedIndex];
  const currentImage = heroImages[displayedIndex];

  useEffect(() => {
    displayedIndexRef.current = displayedIndex;
  }, [displayedIndex]);

  useEffect(() => {
    heroImages.forEach((src) => {
      void preloadHeroImage(src);
    });
  }, []);

  const transitionToIndex = useCallback(async (targetIndex: number) => {
    if (isAnimatingRef.current || targetIndex === displayedIndexRef.current) return;

    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    isAnimatingRef.current = true;
    setIsAnimating(true);

    // Ensure image is fully decoded BEFORE starting exit animation
    await preloadHeroImage(heroImages[targetIndex]);

    if (!isMountedRef.current) return;

    setPhase('exit');

    animationTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;

      setDisplayedIndex(targetIndex);
      setPhase('enter');

      animationTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;

        setPhase('idle');
        isAnimatingRef.current = false;
        setIsAnimating(false);
      }, 50);
    }, 250);
  }, []);

  const handleUserInteraction = useCallback(() => {
    setUserInteracting(true);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => setUserInteracting(false), 5000);
  }, []);

  const goToNext = useCallback(() => {
    void transitionToIndex((displayedIndexRef.current + 1) % c.products.length);
  }, [c.products.length, transitionToIndex]);

  const goToPrev = useCallback(() => {
    void transitionToIndex((displayedIndexRef.current - 1 + c.products.length) % c.products.length);
  }, [c.products.length, transitionToIndex]);

  const handleNext = () => {
    handleUserInteraction();
    goToNext();
  };

  const handlePrev = () => {
    handleUserInteraction();
    goToPrev();
  };

  useEffect(() => {
    if (userInteracting || isAnimating) return;
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [goToNext, isAnimating, userInteracting]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  return (
    <div className="w-full overflow-visible">
      {/* Mobile Layout */}
      {/* Small mobile: vertical stack */}
      <div className="flex flex-col items-center gap-5 sm:hidden px-2 max-w-full overflow-visible">
        <div className="flex items-center gap-4 w-full justify-center">
          <button onClick={handlePrev} className="w-10 h-10 rounded-full border-2 border-foreground/30 hover:border-primary hover:bg-primary flex items-center justify-center transition-all duration-300 flex-shrink-0 group focus:outline-none focus-visible:outline-none" aria-label="Previous card">
            <ChevronLeft className="h-5 w-5 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
          </button>

          <LocalizedLink to={`/shop/id-card-generator/${currentProduct.id}`} className="block relative group">
            <div
              className={cn(
                'relative w-44 min-[400px]:w-56 transform-gpu transition-all duration-300 ease-out will-change-[transform,opacity]',
                phase === 'exit'
                  ? 'opacity-0 -translate-x-8 scale-95 -rotate-[16deg]'
                  : phase === 'enter'
                    ? 'opacity-0 -translate-x-8 scale-95 -rotate-[16deg]'
                    : 'opacity-100 translate-x-0 scale-100 -rotate-[6deg]'
              )}
              style={{ transformOrigin: 'center center', backfaceVisibility: 'hidden', filter: 'drop-shadow(1px 12px 20px rgba(0,0,0,0.45))' }}
            >
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={currentImage}
                  alt={currentProduct.name}
                  width={507}
                  height={324}
                  className="w-full h-auto block"
                  loading="eager"
                  decoding="sync"
                />
              </div>
            </div>
          </LocalizedLink>

          <button onClick={handleNext} className="w-10 h-10 rounded-full border-2 border-foreground/30 hover:border-primary hover:bg-primary flex items-center justify-center transition-all duration-300 flex-shrink-0 group focus:outline-none focus-visible:outline-none" aria-label="Next card">
            <ChevronRight className="h-5 w-5 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
          </button>
        </div>

        <div
          className={cn(
            'text-center transition-all duration-300 ease-out will-change-[transform,opacity]',
            phase !== 'idle' ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'
          )}
        >
          <div className="text-foreground/70 text-lg font-display">€{currentProduct.price}</div>
          <span className="block font-display text-xl font-black uppercase text-foreground leading-tight mt-1">{currentProduct.name}</span>
        </div>

        <Button asChild size="lg" className="px-10">
          <LocalizedLink to="/shop">{c.viewAll}</LocalizedLink>
        </Button>
      </div>

      {/* Desktop & Tablet Layout */}
      <div className="hidden sm:flex items-center gap-6 lg:gap-10 overflow-visible">
        <button onClick={handlePrev} className="w-12 h-12 lg:w-14 lg:h-14 rounded-full border-2 border-foreground/30 hover:border-primary hover:bg-primary flex items-center justify-center transition-all duration-300 hover:scale-110 flex-shrink-0 group focus:outline-none focus-visible:outline-none" aria-label="Previous card">
          <ChevronLeft className="h-6 w-6 lg:h-7 lg:w-7 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
        </button>

        <div className="flex items-center gap-8 lg:gap-12 shrink-0 overflow-visible">
          <LocalizedLink to={`/shop/id-card-generator/${currentProduct.id}`} className="block relative group">
            <div
              className={cn(
                'relative w-56 lg:w-80 xl:w-96 transform-gpu transition-all duration-300 ease-out will-change-[transform,opacity]',
                phase === 'exit'
                  ? 'opacity-0 -translate-x-12 scale-95 -rotate-[18deg]'
                  : phase === 'enter'
                    ? 'opacity-0 -translate-x-12 scale-95 -rotate-[18deg]'
                    : 'opacity-100 translate-x-0 scale-100 -rotate-[12deg]'
              )}
              style={{ transformOrigin: 'center center', backfaceVisibility: 'hidden', filter: 'drop-shadow(2px 16px 24px rgba(0,0,0,0.45))' }}
            >
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={currentImage}
                  alt={currentProduct.name}
                  width={507}
                  height={324}
                  className="w-full h-auto block"
                  loading="eager"
                  decoding="sync"
                />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300" />
              </div>
            </div>
          </LocalizedLink>

          <div
            className={cn(
              'space-y-4 lg:space-y-5 transition-all duration-300 ease-out min-w-[180px] lg:min-w-[220px] shrink-0 will-change-[transform,opacity]',
              phase !== 'idle' ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'
            )}
          >
            <div className="text-foreground/70 text-xl lg:text-2xl font-display">€{currentProduct.price}</div>
            <h3 className="font-display text-2xl lg:text-3xl xl:text-4xl font-black uppercase text-foreground">{currentProduct.name}</h3>
            <Button asChild size="lg" className="w-full lg:w-auto px-8 lg:px-10">
              <LocalizedLink to="/shop">{c.viewAll}</LocalizedLink>
            </Button>
          </div>
        </div>

        <button onClick={handleNext} className="w-12 h-12 lg:w-14 lg:h-14 rounded-full border-2 border-foreground/30 hover:border-primary hover:bg-primary flex items-center justify-center transition-all duration-300 hover:scale-110 flex-shrink-0 group focus:outline-none focus-visible:outline-none" aria-label="Next card">
          <ChevronRight className="h-6 w-6 lg:h-7 lg:w-7 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
        </button>
      </div>
    </div>
  );
};
